/**
 * @name useRfidReaderCapture
 * @description Hook React para captura de dados de leitores RFID físicos
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/hooks/useRfidReaderCapture.ts
 */

import { useCallback, useEffect, useRef } from "react";
import { RfidReaderCapture, RfidReaderConfig } from "../utils/rfidReaderUtils";

/**
 * Hook React para captura de dados RFID
 */
export const useRfidReaderCapture = (config?: Partial<RfidReaderConfig>) => {
  const readerRef = useRef<RfidReaderCapture | null>(null);

  // Inicializar leitor
  useEffect(() => {
    readerRef.current = new RfidReaderCapture(config);
    return () => {
      if (readerRef.current) {
        readerRef.current.clearBuffer();
      }
    };
  }, []);

  const setOnDataReceived = useCallback((callback: (data: string) => void) => {
    if (readerRef.current) {
      readerRef.current.setOnDataReceived(callback);
    }
  }, []);

  const processInput = useCallback((char: string) => {
    if (readerRef.current) {
      readerRef.current.processInput(char);
    }
  }, []);

  const clearBuffer = useCallback(() => {
    if (readerRef.current) {
      readerRef.current.clearBuffer();
    }
  }, []);

  const getBufferState = useCallback(() => {
    return readerRef.current
      ? readerRef.current.getBufferState()
      : { buffer: "", length: 0, isReading: false };
  }, []);

  return {
    setOnDataReceived,
    processInput,
    clearBuffer,
    getBufferState,
  };
};

export default useRfidReaderCapture;
